import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridRow,
    Field,
    Input,
    Label,
    Spinner,
    TableCellLayout,
    TableColumnDefinition,
    createTableColumn,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Text,
    Card,
    Button,
    Dialog,
    DialogTrigger,
    DialogSurface,
    DialogTitle,
    DialogBody,
    DialogActions,
    DialogContent,
    Switch
} from '@fluentui/react-components';
import { ArrowRight24Filled, Person24Regular, Building24Regular, CheckmarkCircle24Filled, MoneyHand24Regular, Dismiss24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

// Component functions
import * as ComponentFunctions from './DebtSelector.Functions';

// Models
import { Debt } from '../../../models/Entities/CashFlow/Debt/Debt';
import { DebtPaymentImputation } from '../../../models/Entities/CashFlow/Payment/DebtPaymentImputation';

// Styles
import './DebtSelector.Styles.css';

interface DebtImputationRow extends Debt {
    amount: number;
    exchangeRate: number;
}

const DebtSelectorComponent = (props: ComponentFunctions.Props) => {
    const [t] = useTranslation();
    const [debtImputations, setDebtImputations] = useState<Map<number, DebtPaymentImputation>>(new Map());
    const [lastExchangeRate, setLastExchangeRate] = useState<number>(1);
    const [autoImputeDialogOpen, setAutoImputeDialogOpen] = useState<boolean>(false);
    const [autoImputeExchangeRate, setAutoImputeExchangeRate] = useState<string>('');
    const [isAutoImputeEnabled, setIsAutoImputeEnabled] = useState<boolean>(false);
    const [hasUserSetExchangeRate, setHasUserSetExchangeRate] = useState<boolean>(false);
    const [pendingDebtToSettle, setPendingDebtToSettle] = useState<number | null>(null);

    useEffect(() => {
        // Load debts by debtor
        if (props.debtor?.personIdentifier) {
            props.GetAllDebtsByDebtor(
                props.debtor.personIdentifier,
                props.creditor?.personIdentifier
            );
        }
    }, [props.debtor?.personIdentifier, props.creditor?.personIdentifier]);

    useEffect(() => {
        // Initialize with selected imputations if provided
        // Convert amounts from debt currency to payment currency for internal state
        if (props.selectedImputations && props.selectedImputations.length > 0) {
            const pendingDebts = getPendingDebts();
            const imputationsMap = new Map<number, DebtPaymentImputation>();
            let hasFx = false;

            props.selectedImputations.forEach(imp => {
                // Find the debt to get its currency
                const debt = pendingDebts.find(d => d.id === imp.debtId);
                
                if (debt) {
                    const sameCurrency = debt.currencyCode === props.paymentCurrency;
                    if (sameCurrency) {
                        imputationsMap.set(imp.debtId, {
                            ...imp,
                            amount: imp.amount // Amount already in payment currency
                        });
                    } else {
                        hasFx = true;
                        // Convert from debt currency to payment currency
                        // amountInPaymentCurrency = amountInDebtCurrency * exchangeRate
                        const amountInPaymentCurrency = imp.amount * imp.exchangeRate;
                        imputationsMap.set(imp.debtId, {
                            ...imp,
                            amount: amountInPaymentCurrency
                        });
                    }
                }
            });
            
            if (hasFx) {
                setHasUserSetExchangeRate(true);
            }

            setDebtImputations(imputationsMap);
        }
    }, [props.selectedImputations, props.paymentCurrency]);

    useEffect(() => {
        // Recalculate imputations when amount/currency or debts change, but only if FX rate is known to avoid mid-typing prompts
        if (!isAutoImputeEnabled || props.paymentAmount <= 0) return;

        const differentCurrency = needsDifferentCurrency();
        if (differentCurrency && !hasUserSetExchangeRate) return;

        handleAutoImpute();
    }, [props.paymentAmount, props.paymentCurrency, props.debts, isAutoImputeEnabled, hasUserSetExchangeRate]);

    const getPendingDebts = () => {
        if (!props.debts) return [];
        // Hide fully settled debts even if the settled flag is not set
        return props.debts.filter(debt => !debt.isDebtSettled && debt.pendingAmount > 0);
    };

    const handleAmountChange = (debtId: number, newAmount: string, debtCurrency: string) => {
        // If currencies differ and we don't have FX yet, prompt before accepting amounts
        if (props.paymentCurrency !== debtCurrency && !hasUserSetExchangeRate) {
            setAutoImputeDialogOpen(true);
            return;
        }

        // Keep empty string as valid input for user to type
        if (newAmount === '') {
            const updatedImputations = new Map(debtImputations);
            updatedImputations.delete(debtId);
            setDebtImputations(updatedImputations);
            notifyImputationsChange(updatedImputations);
            return;
        }

        const amount = parseFloat(newAmount);
        if (isNaN(amount) || amount <= 0) {
            return; // Don't update if invalid
        }

        // Calculate total imputed excluding current debt
        let totalOtherImputations = 0;
        debtImputations.forEach((imp, id) => {
            if (id !== debtId) {
                totalOtherImputations += imp.amount;
            }
        });

        // Validate that total would not exceed payment amount
        if (totalOtherImputations + amount > props.paymentAmount) {
            // Cap the amount to available payment
            const maxAllowed = props.paymentAmount - totalOtherImputations;
            if (maxAllowed <= 0) {
                return; // No available payment
            }
            // Don't allow setting more than available
            return;
        }

        const updatedImputations = new Map(debtImputations);
        const currentImputation = updatedImputations.get(debtId);

        // Determine exchange rate based on currency match
        let exchangeRate: number;
        if (props.paymentCurrency === debtCurrency) {
            // Same currency, no exchange needed
            exchangeRate = 1;
        } else {
            // Different currency, use last entered rate or current imputation rate or last global rate
            exchangeRate = currentImputation?.exchangeRate || lastExchangeRate;
        }

        updatedImputations.set(debtId, {
            debtId,
            amount,
            exchangeRate
        });

        setDebtImputations(updatedImputations);
        notifyImputationsChange(updatedImputations);
    };

    const handleClearImputation = (debtId: number) => {
        const updatedImputations = new Map(debtImputations);
        const freedAmount = updatedImputations.get(debtId)?.amount || 0;
        updatedImputations.delete(debtId);

        // If auto imputation is enabled, redistribute freed amount to following debts
        if (isAutoImputeEnabled && freedAmount > 0) {
            const pendingDebts = getPendingDebts();
            const sortedDebts = [...pendingDebts].sort((a, b) =>
                new Date(a.debtDate).getTime() - new Date(b.debtDate).getTime()
            );

            const clearedDebtIndex = sortedDebts.findIndex(d => d.id === debtId);
            if (clearedDebtIndex !== -1) {
                let remainingToDistribute = freedAmount;

                // Redistribute to following debts (from oldest to newest after the cleared debt)
                for (let i = clearedDebtIndex + 1; i < sortedDebts.length && remainingToDistribute > 0; i++) {
                    const debt = sortedDebts[i];
                    const debtExchangeRate = debt.currencyCode === props.paymentCurrency ? 1 : lastExchangeRate;
                    const debtPendingInPaymentCurrency = debt.pendingAmount * debtExchangeRate;

                    // Check current imputation for this debt
                    const currentImputation = updatedImputations.get(debt.id);
                    const currentAmount = currentImputation?.amount || 0;

                    // Calculate how much more can be added to this debt
                    const maxAdditional = debtPendingInPaymentCurrency - currentAmount;

                    if (maxAdditional > 0) {
                        const amountToAdd = Math.min(remainingToDistribute, maxAdditional);
                        updatedImputations.set(debt.id, {
                            debtId: debt.id,
                            amount: currentAmount + amountToAdd,
                            exchangeRate: debtExchangeRate
                        });
                        remainingToDistribute -= amountToAdd;
                    }
                }
            }
        }

        setDebtImputations(updatedImputations);
        notifyImputationsChange(updatedImputations);
    };

    const performSettleDebtFully = (debtId: number, exchangeRateOverride?: number) => {
        const pendingDebts = getPendingDebts();
        const debt = pendingDebts.find(d => d.id === debtId);
        if (!debt) return;

        const effectiveRate = exchangeRateOverride ?? lastExchangeRate;

        const sortedDebts = [...pendingDebts].sort((a, b) =>
            new Date(a.debtDate).getTime() - new Date(b.debtDate).getTime()
        );

        const targetDebtIndex = sortedDebts.findIndex(d => d.id === debtId);
        if (targetDebtIndex === -1) return;

        // Calculate exchange rate and amount needed for this debt
        const debtExchangeRate = debt.currencyCode === props.paymentCurrency ? 1 : effectiveRate;
        const amountNeededInPaymentCurrency = debt.pendingAmount * debtExchangeRate;

        // Step 1: Calculate available payment without using any imputation
        let totalImputedExcludingTarget = 0;
        debtImputations.forEach((imp, id) => {
            if (id !== debtId) {
                totalImputedExcludingTarget += imp.amount;
            }
        });

        let availableAmount = props.paymentAmount - totalImputedExcludingTarget;

        // Step 2: If available is enough, settle the debt with available amount
        if (availableAmount >= amountNeededInPaymentCurrency) {
            const updatedImputations = new Map(debtImputations);
            updatedImputations.set(debtId, {
                debtId,
                amount: amountNeededInPaymentCurrency,
                exchangeRate: debtExchangeRate
            });
            setDebtImputations(updatedImputations);
            notifyImputationsChange(updatedImputations);
            return;
        }

        // Step 3: Not enough available, need to free up from later debts (more recent to older)
        const updatedImputations = new Map(debtImputations);
        let amountToFree = amountNeededInPaymentCurrency - availableAmount;

        // Iterate backwards through debts after the target (from most recent to older)
        for (let i = sortedDebts.length - 1; i > targetDebtIndex && amountToFree > 0; i--) {
            const laterDebt = sortedDebts[i];
            const laterImputation = updatedImputations.get(laterDebt.id);

            if (laterImputation && laterImputation.amount > 0) {
                if (laterImputation.amount >= amountToFree) {
                    // This imputation has enough to cover what we need
                    const newAmount = laterImputation.amount - amountToFree;
                    if (newAmount > 0) {
                        updatedImputations.set(laterDebt.id, {
                            ...laterImputation,
                            amount: newAmount
                        });
                    } else {
                        updatedImputations.delete(laterDebt.id);
                    }
                    availableAmount += amountToFree;
                    amountToFree = 0;
                } else {
                    // Take all from this imputation and continue
                    availableAmount += laterImputation.amount;
                    amountToFree -= laterImputation.amount;
                    updatedImputations.delete(laterDebt.id);
                }
            }
        }

        // Step 4: Apply the maximum possible amount (either full settlement or what's available)
        const amountToApply = Math.min(amountNeededInPaymentCurrency, availableAmount);

        if (amountToApply > 0) {
            updatedImputations.set(debtId, {
                debtId,
                amount: amountToApply,
                exchangeRate: debtExchangeRate
            });
        } else {
            // If no amount can be applied, remove the imputation
            updatedImputations.delete(debtId);
        }

        setDebtImputations(updatedImputations);
        notifyImputationsChange(updatedImputations);
    };

    const handleSettleDebtFully = (debtId: number) => {
        const pendingDebts = getPendingDebts();
        const debt = pendingDebts.find(d => d.id === debtId);
        if (!debt) return;

        // If currencies differ and we don't have FX yet, prompt before applying and remember target
        if (props.paymentCurrency !== debt.currencyCode && !hasUserSetExchangeRate) {
            setPendingDebtToSettle(debtId);
            setAutoImputeDialogOpen(true);
            return;
        }

        performSettleDebtFully(debtId);
    };

    const recalculateFollowingDebts = (startDebtId: number) => {
        if (!isAutoImputeEnabled) return;

        const pendingDebts = getPendingDebts();
        const sortedDebts = [...pendingDebts].sort((a, b) =>
            new Date(a.debtDate).getTime() - new Date(b.debtDate).getTime()
        );

        const startIndex = sortedDebts.findIndex(d => d.id === startDebtId);
        if (startIndex === -1) return;

        // Calculate total imputed up to and including the modified debt
        let totalImputedUpToModified = 0;
        for (let i = 0; i <= startIndex; i++) {
            const imp = debtImputations.get(sortedDebts[i].id);
            if (imp) {
                totalImputedUpToModified += imp.amount;
            }
        }

        // Recalculate following debts
        const updatedImputations = new Map(debtImputations);
        let remainingPayment = props.paymentAmount - totalImputedUpToModified;

        for (let i = startIndex + 1; i < sortedDebts.length; i++) {
            const debt = sortedDebts[i];

            if (remainingPayment <= 0) {
                // No more payment available, remove imputations from remaining debts
                updatedImputations.delete(debt.id);
                continue;
            }

            const debtExchangeRate = debt.currencyCode === props.paymentCurrency ? 1 : lastExchangeRate;
            const debtPendingInPaymentCurrency = debt.pendingAmount * debtExchangeRate;

            if (remainingPayment >= debtPendingInPaymentCurrency) {
                // Can pay the entire debt
                updatedImputations.set(debt.id, {
                    debtId: debt.id,
                    amount: debt.pendingAmount * debtExchangeRate,
                    exchangeRate: debtExchangeRate
                });
                remainingPayment -= debtPendingInPaymentCurrency;
            } else {
                // Partial payment
                updatedImputations.set(debt.id, {
                    debtId: debt.id,
                    amount: remainingPayment,
                    exchangeRate: debtExchangeRate
                });
                remainingPayment = 0;
            }
        }

        setDebtImputations(updatedImputations);
        notifyImputationsChange(updatedImputations);
    };

    const handleExchangeRateChange = (debtId: number, newExchangeRate: string, debtCurrency: string) => {
        // Allow empty string temporarily for user input
        if (newExchangeRate === '') {
            return;
        }

        const exchangeRate = parseFloat(newExchangeRate);
        if (isNaN(exchangeRate) || exchangeRate <= 0) {
            return; // Keep current value if invalid
        }

        // Save as last exchange rate for replication to following debts
        setLastExchangeRate(exchangeRate);

        const updatedImputations = new Map(debtImputations);
        const currentImputation = updatedImputations.get(debtId);

        // Update current imputation if exists
        if (currentImputation) {
            // Calculate the new amount needed in payment currency with the new exchange rate
            const pendingDebts = getPendingDebts();
            const currentDebt = pendingDebts.find(d => d.id === debtId);
            
            if (currentDebt) {
                // Old amount in debt currency
                const oldAmountInDebtCurrency = currentImputation.amount / currentImputation.exchangeRate;
                // New amount in payment currency with new exchange rate, rounded to 2 decimals
                const newAmountInPaymentCurrency = Math.round(oldAmountInDebtCurrency * exchangeRate * 100) / 100;
                
                // Calculate if this change would exceed payment amount
                let totalOtherImputations = 0;
                updatedImputations.forEach((imp, id) => {
                    if (id !== debtId) {
                        totalOtherImputations += imp.amount;
                    }
                });
                
                // If the new amount would exceed payment amount, cap it
                const maxAllowed = props.paymentAmount - totalOtherImputations;
                const adjustedAmount = Math.min(newAmountInPaymentCurrency, maxAllowed);
                
                updatedImputations.set(debtId, {
                    ...currentImputation,
                    amount: adjustedAmount,
                    exchangeRate
                });
            } else {
                updatedImputations.set(debtId, {
                    ...currentImputation,
                    exchangeRate
                });
            }
        }

        // Replicate to all following debts (with different currency and existing amount)
        // ONLY affects debts AFTER the current one, not before
        const pendingDebts = getPendingDebts();
        if (pendingDebts.length > 0 && props.paymentCurrency !== debtCurrency) {
            const currentDebtIndex = pendingDebts.findIndex(d => d.id === debtId);
            if (currentDebtIndex >= 0) {
                // Loop starts at currentDebtIndex + 1, so previous debts are NOT affected
                for (let i = currentDebtIndex + 1; i < pendingDebts.length; i++) {
                    const nextDebt = pendingDebts[i];
                    const nextImputation = updatedImputations.get(nextDebt.id);
                    // Only update if there's an amount and currency is different
                    if (nextImputation && props.paymentCurrency !== nextDebt.currencyCode) {
                        // Recalculate amount with new exchange rate, rounded to 2 decimals
                        const amountInDebtCurrency = nextImputation.amount / nextImputation.exchangeRate;
                        const newAmountInPaymentCurrency = Math.round(amountInDebtCurrency * exchangeRate * 100) / 100;
                        
                        updatedImputations.set(nextDebt.id, {
                            ...nextImputation,
                            amount: newAmountInPaymentCurrency,
                            exchangeRate
                        });
                    }
                }
            }
        }

        setDebtImputations(updatedImputations);
        
        // If auto imputation is enabled, recalculate following debts
        if (isAutoImputeEnabled) {
            // Calculate total imputed up to and including the current debt
            const sortedDebts = [...pendingDebts].sort((a, b) => 
                new Date(a.debtDate).getTime() - new Date(b.debtDate).getTime()
            );
            
            const currentDebtIndex = sortedDebts.findIndex(d => d.id === debtId);
            if (currentDebtIndex !== -1) {
                let totalImputedUpToCurrent = 0;
                for (let i = 0; i <= currentDebtIndex; i++) {
                    const imp = updatedImputations.get(sortedDebts[i].id);
                    if (imp) {
                        totalImputedUpToCurrent += imp.amount;
                    }
                }
                
                // Redistribute remaining payment to following debts
                let remainingPayment = props.paymentAmount - totalImputedUpToCurrent;
                
                for (let i = currentDebtIndex + 1; i < sortedDebts.length; i++) {
                    const debt = sortedDebts[i];
                    
                    if (remainingPayment <= 0) {
                        // No more payment available, remove imputations from remaining debts
                        updatedImputations.delete(debt.id);
                        continue;
                    }
                    
                    const debtExchangeRate = debt.currencyCode === props.paymentCurrency ? 1 : exchangeRate;
                    const debtPendingInPaymentCurrency = Math.round(debt.pendingAmount * debtExchangeRate * 100) / 100;
                    
                    if (remainingPayment >= debtPendingInPaymentCurrency) {
                        // Can pay the entire debt
                        updatedImputations.set(debt.id, {
                            debtId: debt.id,
                            amount: debtPendingInPaymentCurrency,
                            exchangeRate: debtExchangeRate
                        });
                        remainingPayment -= debtPendingInPaymentCurrency;
                    } else {
                        // Partial payment, rounded to 2 decimals
                        const partialAmount = Math.round(remainingPayment * 100) / 100;
                        updatedImputations.set(debt.id, {
                            debtId: debt.id,
                            amount: partialAmount,
                            exchangeRate: debtExchangeRate
                        });
                        remainingPayment = 0;
                    }
                }
            }
        }
        
        setDebtImputations(updatedImputations);
        notifyImputationsChange(updatedImputations);
    };

    const notifyImputationsChange = (imputationsMap: Map<number, DebtPaymentImputation>) => {
        // Final validation: ensure total imputed never exceeds payment amount
        let totalImputed = 0;
        imputationsMap.forEach(imp => {
            totalImputed += imp.amount;
        });

        // If total exceeds payment, cap the last imputation
        if (totalImputed > props.paymentAmount) {
            console.warn('Total imputed exceeds payment amount. Capping imputations.');
            const excess = totalImputed - props.paymentAmount;

            // Find the last imputation and reduce it
            const imputationsArray = Array.from(imputationsMap.entries());
            if (imputationsArray.length > 0) {
                const [lastDebtId, lastImputation] = imputationsArray[imputationsArray.length - 1];
                if (lastImputation.amount > excess) {
                    imputationsMap.set(lastDebtId, {
                        ...lastImputation,
                        amount: lastImputation.amount - excess
                    });
                } else {
                    imputationsMap.delete(lastDebtId);
                }
            }
        }

        // Convert amounts to debt currency before notifying
        const pendingDebts = getPendingDebts();
        const imputationsArray = Array.from(imputationsMap.values())
            .filter(imp => imp.amount > 0)
            .map(imp => {
                // Find the debt to get its currency
                const debt = pendingDebts.find(d => d.id === imp.debtId);
                
                // If debt currency matches payment currency, no conversion needed
                if (debt && debt.currencyCode === props.paymentCurrency) {
                    return {
                        ...imp,
                        amount: Math.round(imp.amount * 100) / 100 // Round to 2 decimals
                    };
                }
                
                // Different currency: amount is in payment currency, convert to debt currency using inverse operation
                // amountInDebtCurrency = amountInPaymentCurrency / exchangeRate
                const amountInDebtCurrency = imp.amount / imp.exchangeRate;
                return {
                    ...imp,
                    amount: Math.round(amountInDebtCurrency * 100) / 100 // Round to 2 decimals
                };
            });
        
        props.onImputationsChange(imputationsArray);
    };

    const getDebtImputation = (debtId: number): DebtPaymentImputation | undefined => {
        return debtImputations.get(debtId);
    };

    const calculateRemainingAmount = (debt: Debt): number => {
        const imputation = getDebtImputation(debt.id);
        if (!imputation || !imputation.amount) {
            return debt.pendingAmount;
        }

        // Convert payment amount to debt currency
        // amount is in payment currency, divide by exchange rate to get debt currency
        const amountInDebtCurrency = imputation.amount / imputation.exchangeRate;
        return debt.pendingAmount - amountInDebtCurrency;
    };

    const needsExchangeRate = (debtCurrency: string): boolean => {
        return props.paymentCurrency !== debtCurrency;
    };

    const columns: TableColumnDefinition<Debt>[] = [
        createTableColumn<Debt>({
            columnId: "debtDate",
            compare: (a, b) => new Date(a.debtDate).getTime() - new Date(b.debtDate).getTime(),
            renderHeaderCell: () => t('debtSelector.debtDate') || "Fecha de Deuda",
            renderCell: (item: Debt) => (
                <TableCellLayout>
                    {moment(item.debtDate).format('DD/MM/YYYY')}
                </TableCellLayout>
            )
        }),
        // createTableColumn<Debt>({
        //     columnId: "originalAmount",
        //     renderHeaderCell: () => t('debtSelector.originalAmount') || "Monto Original",
        //     renderCell: (item: Debt) => (
        //         <TableCellLayout>
        //             {item.originalAmount.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {item.currencyCode}
        //         </TableCellLayout>
        //     )
        // }),
        createTableColumn<Debt>({
            columnId: "pendingAmount",

            renderHeaderCell: () => t('debtSelector.pendingAmount') || "Monto Pendiente",
            renderCell: (item: Debt) => (
                <TableCellLayout>
                    {item.pendingAmount.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {item.currencyCode}
                </TableCellLayout>
            )
        }),
        createTableColumn<Debt>({
            columnId: "amount",
            renderHeaderCell: () => t('debtSelector.amountToApply') || "Monto a Aplicar",
            renderCell: (item: Debt) => (
                <TableCellLayout>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center', minWidth: '100px' }}>
                        <Field style={{ flex: 1 }}>
                            <Input
                                type="number"
                                min={0}
                                step={0.01}
                                value={getDebtImputation(item.id)?.amount?.toString() || '0'}
                                onChange={(e, data) => handleAmountChange(item.id, data.value, item.currencyCode)}
                                onBlur={() => recalculateFollowingDebts(item.id)}
                                onFocus={(e) => e.target.select()}
                                contentAfter={<Text>{props.paymentCurrency}</Text>}
                            />
                        </Field>
                        <Button
                            appearance="subtle"
                            icon={<MoneyHand24Regular />}
                            size="small"
                            title="Saldar deuda completa"
                            onClick={() => handleSettleDebtFully(item.id)}
                        />
                        <Button
                            appearance="subtle"
                            icon={<Dismiss24Regular />}
                            size="small"
                            title="Desimputar deuda"
                            onClick={() => handleClearImputation(item.id)}
                        />
                    </div>
                </TableCellLayout>
            )
        }),
        createTableColumn<Debt>({
            columnId: "exchangeRate",
            renderHeaderCell: () => t('debtSelector.exchangeRate') || "Tipo de Cambio",
            renderCell: (item: Debt) => {
                const imputation = getDebtImputation(item.id);
                const hasImputation = !!imputation;
                const requiresExchangeRate = needsExchangeRate(item.currencyCode);

                if (!requiresExchangeRate) {
                    return (
                        <TableCellLayout>
                            <div style={{ flex: 1 }}>
                                <Text>N/A</Text>
                            </div>
                        </TableCellLayout>
                    );
                }

                return (
                    <TableCellLayout>
                        <Field style={{ flex: 1 }}>
                            <Input
                                type="number"
                                min={0}
                                step={0.000001}
                                value={hasImputation ? (imputation.exchangeRate?.toString() || lastExchangeRate.toString()) : lastExchangeRate.toString()}
                                onChange={(e, data) => handleExchangeRateChange(item.id, data.value, item.currencyCode)}
                                onFocus={(e) => e.target.select()}
                            />
                        </Field>
                    </TableCellLayout>
                );
            }
        }),
        createTableColumn<Debt>({
            columnId: "remainingAmount",
            renderHeaderCell: () => t('debtSelector.remainingAmount') || "Monto Restante",
            renderCell: (item: Debt) => {
                const remaining = calculateRemainingAmount(item);
                const isFullyPaid = remaining === 0;
                return (
                    <TableCellLayout>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {remaining.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {item.currencyCode}
                            {isFullyPaid && (
                                <CheckmarkCircle24Filled style={{ color: '#107c10' }} />
                            )}
                        </div>
                    </TableCellLayout>
                );
            }
        })
    ];

    const columnSizingOptions = {
        amount: {
            minWidth: 140,
        },
        exchangeRate: {
            minWidth: 90,
        },
    };

    // Auto imputation function
    const handleAutoImpute = (exchangeRateForAuto?: number) => {
        const pendingDebts = getPendingDebts();
        if (pendingDebts.length === 0 || !props.paymentAmount || props.paymentAmount <= 0) {
            return;
        }

        // Sort debts by date (oldest first)
        const sortedDebts = [...pendingDebts].sort((a, b) =>
            new Date(a.debtDate).getTime() - new Date(b.debtDate).getTime()
        );

        // Check if different currency
        const differentCurrency = sortedDebts.some(debt => debt.currencyCode !== props.paymentCurrency);

        // If different currency and no FX provided nor previously informed, prompt once
        if (differentCurrency && !exchangeRateForAuto && !hasUserSetExchangeRate) {
            setAutoImputeDialogOpen(true);
            return;
        }

        // Use informed FX (override or last remembered)
        const exchangeRate = exchangeRateForAuto || lastExchangeRate;
        let remainingPayment = props.paymentAmount;
        const newImputations = new Map<number, DebtPaymentImputation>();

        for (const debt of sortedDebts) {
            if (remainingPayment <= 0) break;

            const debtExchangeRate = debt.currencyCode === props.paymentCurrency ? 1 : exchangeRate;

            // Calculate how much of the payment (in payment currency) can be applied to this debt
            const debtPendingInPaymentCurrency = debt.pendingAmount * debtExchangeRate;

            if (remainingPayment >= debtPendingInPaymentCurrency) {
                // Can pay the entire debt
                newImputations.set(debt.id, {
                    debtId: debt.id,
                    amount: debt.pendingAmount * debtExchangeRate,
                    exchangeRate: debtExchangeRate
                });
                remainingPayment -= debtPendingInPaymentCurrency;
            } else {
                // Partial payment
                newImputations.set(debt.id, {
                    debtId: debt.id,
                    amount: remainingPayment,
                    exchangeRate: debtExchangeRate
                });
                remainingPayment = 0;
            }
        }

        setDebtImputations(newImputations);
        setLastExchangeRate(exchangeRate);
        setHasUserSetExchangeRate(true);
        notifyImputationsChange(newImputations);
        setAutoImputeDialogOpen(false);
    };

    const confirmAutoImpute = () => {
        const rate = parseFloat(autoImputeExchangeRate);
        if (!isNaN(rate) && rate > 0) {
            // Persist the informed FX in state for reuse
            setHasUserSetExchangeRate(true);
            setLastExchangeRate(rate);
            setAutoImputeExchangeRate(rate.toString());

            if (pendingDebtToSettle !== null) {
                performSettleDebtFully(pendingDebtToSettle, rate);
                setPendingDebtToSettle(null);
                setAutoImputeDialogOpen(false);
                return;
            }

            handleAutoImpute(rate);
        }
    };

    const needsDifferentCurrency = () => {
        const pendingDebts = getPendingDebts();
        return pendingDebts.some(debt => debt.currencyCode !== props.paymentCurrency);
    };

    // Prompt for FX as soon as debts are present with different currency and no FX informed yet
    useEffect(() => {
        if (props.debts && props.debts.length > 0) {
            const differentCurrency = needsDifferentCurrency();
            if (differentCurrency && !hasUserSetExchangeRate) {
                // Prefill with last known FX so user can confirm or adjust
                setAutoImputeExchangeRate(lastExchangeRate ? lastExchangeRate.toString() : '');
                setAutoImputeDialogOpen(true);
            }
        }
    }, [props.debts, props.paymentCurrency, hasUserSetExchangeRate, lastExchangeRate]);

    if (props.isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <Spinner label={t('debtSelector.loading') || "Cargando deudas..."} />
            </div>
        );
    }

    if (props.error) {
        return (
            <MessageBar intent="error">
                <MessageBarBody>
                    <MessageBarTitle>{t('debtSelector.error') || "Error"}</MessageBarTitle>
                    {props.error.ErrorMessage}
                </MessageBarBody>
            </MessageBar>
        );
    }

    if (!props.debts || props.debts.length === 0) {
        return (
            <MessageBar intent="info">
                <MessageBarBody>
                    {t('debtSelector.noDebts') || "No hay deudas disponibles para este deudor"}
                </MessageBarBody>
            </MessageBar>
        );
    }

    const pendingDebts = getPendingDebts();

    if (pendingDebts.length === 0) {
        return (
            <MessageBar intent="success">
                <MessageBarBody>
                    {t('debtSelector.allDebtsSettled') || "Todas las deudas han sido saldadas"}
                </MessageBarBody>
            </MessageBar>
        );
    }

    // Get debtor and creditor information
    const getDebtorInfo = () => {
        return {
            name: props.debtor?.businessName || props.debtor?.personIdentifier || 'Deudor',
            isBusiness: props.debtor?.personType === 1 || !!props.debtor?.businessName
        };
    };

    const getCreditorInfo = () => {
        return {
            name: props.creditor?.businessName || props.creditor?.personIdentifier || 'Acreedor',
            isBusiness: props.creditor?.personType === 1 || !!props.creditor?.businessName
        };
    };

    const debtorInfo = getDebtorInfo();
    const creditorInfo = getCreditorInfo();

    return (
        <div className="debt-selector-container">
            <Label size="large" weight="semibold">
                {t('debtSelector.title') || "Seleccionar Deudas para Imputación"}
            </Label>

            <Card
                style={{
                    marginTop: '12px',
                    marginBottom: '16px',
                    padding: '16px',
                    backgroundColor: '#f5f5f5'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                        {debtorInfo.isBusiness ? <Building24Regular /> : <Person24Regular />}
                        <div style={{ textAlign: 'center' }}>
                            <Text size={200} style={{ display: 'block', color: '#666' }}>Pagador (Deudor)</Text>
                            <Text weight="semibold" size={400}>{debtorInfo.name}</Text>
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                        <ArrowRight24Filled style={{ color: '#0078d4' }} />
                        <Text size={200} style={{ color: '#0078d4' }}>Paga a</Text>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                        {creditorInfo.isBusiness ? <Building24Regular /> : <Person24Regular />}
                        <div style={{ textAlign: 'center' }}>
                            <Text size={200} style={{ display: 'block', color: '#666' }}>Receptor (Acreedor)</Text>
                            <Text weight="semibold" size={400}>{creditorInfo.name}</Text>
                        </div>
                    </div>
                </div>
            </Card>

            <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                <Switch
                    checked={isAutoImputeEnabled}
                    onChange={(e, data) => {
                        setIsAutoImputeEnabled(data.checked);
                        if (data.checked) {
                            handleAutoImpute();
                        }
                    }}
                    label="Imputación Automática"
                />
                <Text size={300}>
                    Disponible: {(() => {
                        const totalImputed = Array.from(debtImputations.values()).reduce((sum, imp) => sum + (imp.amount || 0), 0);
                        const remaining = props.paymentAmount - totalImputed;
                        const remainingFormatted = remaining.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        const totalFormatted = props.paymentAmount.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        return `${remainingFormatted} de ${totalFormatted}`;
                    })()} {props.paymentCurrency}
                </Text>
            </div>

            <Dialog open={autoImputeDialogOpen} onOpenChange={(_, data) => setAutoImputeDialogOpen(data.open)}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>Tipo de Cambio para Imputación Automática</DialogTitle>
                        <DialogContent>
                            <Text style={{ marginBottom: '12px', display: 'block' }}>
                                Las deudas tienen una moneda diferente al pago ({props.paymentCurrency}).
                                Por favor, ingrese el tipo de cambio a utilizar:
                            </Text>
                            <Field label="Tipo de Cambio" required>
                                <Input
                                    type="number"
                                    step={0.000001}
                                    value={autoImputeExchangeRate}
                                    onChange={(e, data) => setAutoImputeExchangeRate(data.value)}
                                    placeholder="Ej: 1.5"
                                />
                            </Field>
                        </DialogContent>
                        <DialogActions>
                            <Button appearance="secondary" onClick={() => setAutoImputeDialogOpen(false)}>
                                Cancelar
                            </Button>
                            <Button appearance="primary" onClick={confirmAutoImpute}>
                                Confirmar
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>

            <div className="debt-selector-table-wrapper">
                <DataGrid
                    items={pendingDebts}
                    columns={columns}
                    resizableColumns
                    getRowId={(item) => item.id.toString()}
                    columnSizingOptions={columnSizingOptions}
                >
                    <DataGridHeader>
                        <DataGridRow>
                            {({ renderHeaderCell }) => (
                                <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                            )}
                        </DataGridRow>
                    </DataGridHeader>
                    <DataGridBody<Debt>>
                        {({ item, rowId }) => {
                            const hasImputation = getDebtImputation(item.id)?.amount && getDebtImputation(item.id)!.amount > 0;
                            const remaining = calculateRemainingAmount(item);
                            const isFullyPaid = remaining === 0;

                            let rowClass = '';
                            if (isFullyPaid) {
                                rowClass = 'debt-row-fully-paid';
                            } else if (hasImputation) {
                                rowClass = 'debt-row-with-imputation';
                            }

                            return (
                                <DataGridRow<Debt>
                                    key={rowId}
                                    className={rowClass}
                                >
                                    {({ renderCell }) => (
                                        <DataGridCell>{renderCell(item)}</DataGridCell>
                                    )}
                                </DataGridRow>
                            );
                        }}
                    </DataGridBody>
                </DataGrid>
            </div>
        </div>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(DebtSelectorComponent as any) as React.ComponentType<ComponentFunctions.OwnProps>;
